import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import {
  ACTION_DENIED_MESSAGE,
  CONNECTIVITY_OR_OFFLINE_MESSAGE,
  OFFLINE_IPW_DENY_MESSAGE,
  isActionForbidden,
  isOnlineOnlyOfflineDenial,
} from '../lib/action-denied.js';
import {
  logSelfServeTransferFailure,
  selfServeTransferErrorMessage,
} from '../lib/self-serve-transfer-error.js';

/**
 * SCRN 22 — Internet Password (CCF-17): generate a temporary internet password
 * for an identified customer. Two paths, faithful to the Figma:
 *   - Transfer to self serve — scripting + a "Transfer to automated self-service"
 *     action (the IVR self-service hand-off; surfaced as a toast — there's no
 *     telephony transfer in the desktop build).
 *   - Create manual password — scripting + "Generate manual internet password",
 *     which calls the BFF (`POST /api/security/internet-password` →
 *     `password/reset`) and displays the returned 4-char password for the agent
 *     to read out. New passwords can be generated without limit.
 *
 * The shared agent banner lives in the app shell, so this renders only `<main>`.
 */
export function InternetPasswordScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  const customerNumber = customer.cif ?? '';
  const cardNumber = customer.onlineCardNumber ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [option, setOption] = useState<'' | 'able' | 'unable'>('');
  const [scriptingComplete, setScriptingComplete] = useState(false);
  const [password, setPassword] = useState('');
  const [generated, setGenerated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // #452 — the real self-service transfer state (inline beside the button).
  const [transferring, setTransferring] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferred, setTransferred] = useState(false);
  // #452 — synchronous re-entry guard for the transfer click (debounce). A ref
  // (not state) so a double-click within the same tick is dropped before the
  // disabled state paints.
  const transferInFlight = useRef(false);

  const open24Nr = customer.onlineCardNumber ?? '';

  /**
   * #452 — perform the REAL Genesys blind transfer of the caller back to the IVR
   * self-service flow (ONLINE-ONLY). The BFF attaches `Verification=Pan` +
   * `Open24Nr` to the live conversation and blind-transfers the caller. Results
   * surface INLINE beside the button (#80/#82/#94): a forbidden shows dylan's
   * verbatim copy; any other failure shows the code + message; success shows a
   * confirmation. The button stays (recoverable).
   */
  async function transferToSelfServe(): Promise<void> {
    // #452 — click DEBOUNCE. The button is disabled while in-flight, but a
    // synchronous double-click can fire the handler again before React paints
    // the disabled state; the ref guard drops the re-entrant click so the blind
    // transfer fires ONCE (there was a ~4× client retry storm on this button).
    if (transferInFlight.current || transferred) return;
    transferInFlight.current = true;
    setTransferring(true);
    setTransferError('');
    setTransferred(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/genesys/transfer-to-self-serve', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: open24Nr ? { open24Nr } : {},
      });
      // #611 — an ONLINE-ONLY action refused ONLY because the state resolved
      // Offline (the #628 `onlineWouldAllow` signal) is a connectivity/offline
      // problem, NOT a role-level permission gap. Show the connectivity/offline
      // copy — never the permission copy (@dylan219, 2026-08-13). A genuine
      // role-level `forbidden` (no `onlineWouldAllow`) keeps the permission copy.
      if (isOnlineOnlyOfflineDenial(response.status, apiError)) {
        setTransferError(CONNECTIVITY_OR_OFFLINE_MESSAGE);
        return;
      }
      if (isActionForbidden(response.status, apiError)) {
        setTransferError(ACTION_DENIED_MESSAGE);
        return;
      }
      if (apiError || !data || (data as { ok?: boolean }).ok !== true) {
        // #731 (@dylan219) — mirror the failure (incl. the BFF's `detail`
        // diagnostic) to the browser console so the tester's console-log
        // capture carries it; the body itself is already in the HAR/network
        // capture now that the BFF returns `detail` in the 502/503 bodies.
        logSelfServeTransferFailure(response.status, apiError);
        // #611 — surface the SPECIFIC BFF failure reason (with a `(ref: …)` tag)
        // so the agent can see exactly what error occurred, not a generic
        // catch-all. All non-blocking — the button stays so the agent can retry.
        setTransferError(
          selfServeTransferErrorMessage(response.status, apiError),
        );
        return;
      }
      setTransferred(true);
      toast.success('Transferring to automated self-service');
    } catch {
      setTransferError('Could not transfer to self-service. Please try again.');
    } finally {
      transferInFlight.current = false;
      setTransferring(false);
    }
  }

  function choose(next: 'able' | 'unable'): void {
    setOption(next);
    setScriptingComplete(false);
    setPassword('');
    setGenerated(false);
  }

  async function generate(): Promise<void> {
    if (!hasContext) {
      setError('No verified customer in context. Complete ID&V first.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/security/internet-password', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, cardNumber },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // #805 (@dylan219, 2026-09-16): the write is ONLINE-ONLY (#734), so an
        // agent who HOLDS the grant but whose session resolves Offline is
        // refused with the #628 `onlineWouldAllow` signal — a missing live call,
        // not a role-level permission gap. Check that FIRST and show the exact
        // offline-status copy beside the button. A genuine role-level
        // `forbidden` (no `onlineWouldAllow`) keeps the #248 action-denied copy;
        // a non-forbidden 403 (the Teller-ID gate's `invalid_teller_id`) keeps
        // its own distinct message; any other failure keeps the retry toast.
        if (isOnlineOnlyOfflineDenial(response.status, apiError)) {
          setError(OFFLINE_IPW_DENY_MESSAGE);
          return;
        }
        if (isActionForbidden(response.status, apiError)) {
          setError(ACTION_DENIED_MESSAGE);
          return;
        }
        toast.error(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : 'Could not generate a password. Please try again.',
        );
        return;
      }
      setPassword((data as { Password?: string }).Password ?? '');
      setGenerated(true);
    } catch {
      toast.error('Could not generate a password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      className="max-w-[1400px] mx-auto px-12 py-12"
      aria-labelledby="ipw-heading"
    >
      <div className="mb-8">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="ipw-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6"
        >
          Internet password
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      <div className="flex gap-4 mb-6">
        <button
          type="button"
          onClick={() => choose('able')}
          aria-label="Transfer to self serve"
          className={`px-6 py-3 rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
            option === 'able'
              ? 'bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E]'
              : 'bg-white text-[#FF703E] border-2 border-[#FF703E] hover:bg-[#fff5f2]'
          }`}
        >
          Transfer to self serve
        </button>
        <button
          type="button"
          onClick={() => choose('unable')}
          aria-label="Create manual password"
          className={`px-6 py-3 rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
            option === 'unable'
              ? 'bg-[#FF703E] text-[#1D252C] border-2 border-[#FF703E]'
              : 'bg-white text-[#FF703E] border-2 border-[#FF703E] hover:bg-[#fff5f2]'
          }`}
        >
          Create manual password
        </button>
      </div>

      {/* Transfer to self serve (CCF-17 A). */}
      {option === 'able' && (
        <div className="bg-white border-2 border-[#979c9f] p-6 shadow-sm max-w-[800px]">
          <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            Transfer to self serve
          </h2>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-2">
            I&apos;ll transfer you now to our automated self service. It will
            provide you with your internet password.
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            Please listen carefully to the instructions on the automated self
            service.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <input
              type="checkbox"
              id="ipw-scripting-able"
              checked={scriptingComplete}
              onChange={() => setScriptingComplete((c) => !c)}
              className="size-[20px] accent-[#FF703E] cursor-pointer"
            />
            <label
              htmlFor="ipw-scripting-able"
              className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
            >
              Scripting complete
            </label>
          </div>
          {scriptingComplete && (
            <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
              <button
                type="button"
                disabled={transferring || transferred}
                onClick={() => void transferToSelfServe()}
                className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {transferring
                  ? 'Transferring…'
                  : 'Transfer to automated self-service'}
              </button>
              {/* #452 — inline result beside the button (#80/#82/#94). */}
              {transferError && (
                <p
                  role="alert"
                  className="mt-4 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                >
                  {transferError}
                </p>
              )}
              {transferred && (
                <p
                  role="status"
                  className="mt-4 text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Caller transferred to automated self-service.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Create manual password (CCF-17 B). */}
      {option === 'unable' && (
        <div className="bg-white border-2 border-[#979c9f] p-6 shadow-sm max-w-[800px]">
          <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4">
            Create manual password
          </h2>
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2">
            Before I call out your manual internet password I need to make you
            aware that:
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
            You need to log in on a PC or laptop for the first time.
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
            The password will remain active for 30 days.
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
            PTSB will never ask you to confirm the full six digits of your
            Personal Access Number, we will only ever ask you for three random
            digits from this number. PTSB will also never contact you by email
            or text message asking you to confirm or update any personal
            details.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <input
              type="checkbox"
              id="ipw-scripting-unable"
              checked={scriptingComplete}
              onChange={() => setScriptingComplete((c) => !c)}
              className="size-[20px] accent-[#FF703E] cursor-pointer"
            />
            <label
              htmlFor="ipw-scripting-unable"
              className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
            >
              Scripting complete
            </label>
          </div>

          {scriptingComplete && !generated && (
            <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
              <button
                type="button"
                disabled={submitting}
                onClick={() => void generate()}
                className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Working…' : 'Generate manual internet password'}
              </button>
            </div>
          )}

          {generated && (
            <div className="mt-6 pt-6 border-t border-[#e5e7eb]">
              <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                Internet password:
              </h3>
              <p
                className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] tracking-wider mb-4 font-mono"
                aria-label="Generated internet password"
              >
                {password}
              </p>
              <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-4">
                This password will take 10 minutes to activate.
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => void generate()}
                  className="px-6 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#f3f4f6] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Generate a new password
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-[4px] font-['Arial',sans-serif] font-bold text-[16px] hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
